// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import Loading from '../../../components/Loading';
import { DataTable } from '../../../components/Table/DataTable';
import useBlacklistedUsers from '../../../hooks/queries/admin/useBlacklistedUsers';
import useRemoveBlacklistedUsers from '../../../hooks/mutations/admin/useRemoveBlacklistedUsers';
import { BlacklistedUser } from '../../../utils/operationalApi';
import useMutate from '../../../hooks/mutations/useMutate';
import { BlacklistUserDialog } from './BlacklistUserDialog';
import { ReprocessAttestationsDialog } from './ReprocessAttestationsDialog';
import { SyncButton } from '../../../components/Button/SyncButton';
import { CreateDepositAttestationDialog } from './CreateDepositAttestationDialog';
import useBridgeAgreement from '../../../hooks/queries/bridge/agreement/useBridgeAgreement';

type BlacklistRow = BlacklistedUser;

const createBlacklistHeaders = (
  onRemove: (partyId: string) => void,
): GridColDef<BlacklistRow>[] => [
  {
    field: 'partyId',
    headerName: 'Party ID',
    flex: 300,
    filterable: true,
  },
  {
    field: 'reason',
    headerName: 'Reason',
    flex: 200,
    filterable: false,
  },
  {
    field: 'createdAt',
    headerName: 'Blacklisted At',
    flex: 150,
    filterable: false,
    valueFormatter: (params) => {
      const value = params.value as string;
      return value ? new Date(value).toLocaleString() : '';
    },
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    flex: 80,
    filterable: false,
    renderCell: (params: GridRenderCellParams<BlacklistRow>) => (
      <IconButton
        color="error"
        onClick={() => onRemove(params.row.partyId)}
        title="Remove from blacklist"
      >
        <DeleteIcon />
      </IconButton>
    ),
  },
];

export const Admin = () => {
  const blacklistedUsers = useBlacklistedUsers();
  const removeBlacklistedUsersMutation = useRemoveBlacklistedUsers();
  const mutate = useMutate();
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [depositAttestationIsOpen, setDepositAttestationIsOpen] = useState(false);
  const bridgeAgreement = useBridgeAgreement();

  const handleRemoveFromBlacklist = async (partyId: string) => {
    await mutate(
      removeBlacklistedUsersMutation,
      [partyId],
      'User removed from blacklist',
      'Failed to remove user from blacklist',
    );
  };
  const isLoading =
    bridgeAgreement.isLoading ||
    blacklistedUsers.isLoading ||
    !bridgeAgreement.data;
  if (isLoading) return <Loading />;

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ mt: 2, mb: 2 }} color="primary" variant="h3">
          Deposit Attestations
        </Typography>
        <SyncButton sx={{ mr: 1 }}  onClick={() => setDepositAttestationIsOpen(true)}>
          Submit Deposit Attestation
        </SyncButton>
        <SyncButton onClick={() => setReprocessDialogOpen(true)}>
          Reprocess Deposit Attestations
        </SyncButton>
      </Box>

      <DataTable
        enableTableWrapper
        title="Blacklisted Users"
        variant="h3"
        columns={createBlacklistHeaders(handleRemoveFromBlacklist)}
        rows={blacklistedUsers.data ?? []}
        rowKey={(row) => String(row.id)}
        toolbar={() => (
          <Box sx={{ p: 1 }}>
            <SyncButton onClick={() => setBlacklistDialogOpen(true)}>
              Blacklist User
            </SyncButton>
          </Box>
        )}
      />

      <BlacklistUserDialog
        open={blacklistDialogOpen}
        onClose={() => setBlacklistDialogOpen(false)}
      />
      <ReprocessAttestationsDialog
        open={reprocessDialogOpen}
        onClose={() => setReprocessDialogOpen(false)}
      />
      <CreateDepositAttestationDialog
        open={depositAttestationIsOpen}
        onClose={() => setDepositAttestationIsOpen(false)}
        bridgeAgreement={bridgeAgreement.data}
      />
    </>
  );
};
